/**
 * @fileoverview The instructor Programs route.
 *
 * The path carries the instructor's own user id — `/instructors/:userId/programs`
 * — rather than sitting at `/programs`, which is already the PUBLIC student
 * directory (`/programs/:programId`). Two dynamic segments at the same position
 * in one router would make whichever route is declared second unreachable.
 *
 * The API only ever answers for the caller (`Depends(get_current_user)`), so a
 * mismatched id in the URL would render the caller's own data under someone
 * else's address. The page normalizes instead — see ProgramsPage.
 */

/** Route pattern registered in App.jsx. */
export const PROGRAMS_ROUTE = "/instructors/:userId/programs";

/** The canonical Programs URL for a signed-in instructor. */
export function programsPath(user) {
  const id = user?.user_id;
  // "me" is the placeholder for a not-yet-resolved user; the page swaps it for
  // the real id once auth lands, so a click during load never 404s.
  return `/instructors/${id || "me"}/programs`;
}

/** The Programs URL for one program's detail view. */
export function programDetailPath(user, programId) {
  return `${programsPath(user)}/${programId}`;
}

/**
 * The URL of one class's sessions page, under its program (I8). A class is
 * reached from its program and goes back to it, so its address says so — and
 * the Programs item stays lit while it is open.
 */
export function programClassPath(user, programId, classId) {
  return `${programDetailPath(user, programId)}/classes/${classId}`;
}

/**
 * The digit on the Programs nav item.
 *
 * Three unanswered things live behind that one destination — a program
 * invitation, an organization invitation, and a change an org has made to a
 * program this instructor teaches in — so they sum into one number rather than
 * competing for the same corner of the same icon. The tabs inside say which is
 * which; the badge only says "there is something here".
 *
 * Each part is resolved server-side on /auth/me and is already zeroed there
 * when its surface is switched off, so this never has to know about flags.
 */
export function programsBadgeCount(user) {
  return (
    (user?.pending_program_invites || 0) +
    (user?.pending_org_invites || 0) +
    (user?.unseen_program_updates || 0)
  );
}
